import RateReviewIcon from "@mui/icons-material/RateReview";
import { DialogActions, DialogContent } from "@mui/material";
import {
  Button,
  CreatedUpdatedBy,
  Field,
  FieldsContainer,
  Form,
  PageHeader,
  TextField,
} from "common";
import { ParamsContext } from "common/components/Form/ParamsContext";
import HistoryListDialog from "features/histories/HistoryListDialog";
import useAccess from "features/profile/useAccess";
import I18n from "i18n";
import { createContext } from "react";
import useReviewPartnerForm, {
  UseReviewPartnerFormProps,
  UseReviewPartnerFormReturnType,
} from "./useReviewPartnerForm";

export const ReviewPartnerContext = createContext<
  UseReviewPartnerFormReturnType | undefined
>(undefined);

export interface ReviewPartnerFormProps extends UseReviewPartnerFormProps {}

export default function ReviewPartnerForm({
  id,
  afterSave,
  onClose,
  defaultValues,
}: ReviewPartnerFormProps) {
  const { hasPermissions, canEditItem } = useAccess();
  const form = useReviewPartnerForm({
    id,
    afterSave,
    defaultValues,
  });
  const {
    params,
    item,
    options,
    errors,
    loading,
    isDirty,
    isCreate,
    isUpdate,
    getIsDirty,
    getRequiredFields,
    onChange,
    save,
    saving,
  } = form;
  const title = isCreate ? I18n.t("ReviewPartner.addTitle") : item.name;
  const subtitle = isCreate ? undefined : undefined;

  return (
    <ParamsContext.Provider value={{ review_partner_id: id }}>
      <ReviewPartnerContext.Provider value={form}>
        <PageHeader
          avatar={<RateReviewIcon />}
          title={title}
          subtitle={subtitle}
          onClose={onClose}
          loading={loading}
        />
        <DialogContent>
          <Form
            loading={loading}
            item={item}
            options={options}
            errors={errors}
            onChange={onChange}
            translationCategory="ReviewPartner.attributes"
            translationPlaceholdersCategory="ReviewPartner.placeholders"
            translationHelpersCategory="ReviewPartner.helpers"
            isDirty={getIsDirty}
            requiredFields={getRequiredFields()}
            disabled={!canEditItem("review_partner", item)}
          >
            <FieldsContainer>
              <Field fullWidth>
                <TextField id="name" />
              </Field>
            </FieldsContainer>
          </Form>
        </DialogContent>
        <DialogActions>
          {isUpdate && (
            <CreatedUpdatedBy
              objectName="ReviewPartner"
              item={item}
              sx={{
                p: 1,
                paddingTop: 0,
                paddingBottom: 0.5,
                marginRight: "auto",
              }}
              renderDialog={
                hasPermissions(["history.viewany", "history.viewown"])
                  ? props => <HistoryListDialog {...props} subtitle={title} />
                  : undefined
              }
            />
          )}
          <Button
            title={I18n.t("App.save")}
            disabled={!isDirty}
            onClick={() => save()}
            loading={saving}
          />
        </DialogActions>
      </ReviewPartnerContext.Provider>
    </ParamsContext.Provider>
  );
}
